import { useEffect, useRef } from 'react';

const MARKS = {
  POLL_START: 'sfly:header:zuul-poll-start',
  ZUUL_FOUND: 'sfly:header:zuul-found',
  ZUUL_TIMEOUT: 'sfly:header:zuul-timeout',
  INIT_START: 'sfly:header:zuul-init-start',
  INIT_DONE: 'sfly:header:zuul-init-done',
  GET_TOKEN_START: 'sfly:header:get-token-start',
  GET_TOKEN_DONE: 'sfly:header:get-token-done',
  AUTH_RESOLVED: 'sfly:header:auth-resolved',
} as const;

export type DomainName = 'BUILDER' | 'CART' | 'SHOPPING' | 'PHOTOS';
type AuthResult = 'RESOLVED' | 'ZUUL_TIMEOUT';

interface HeaderTimingReporterProps {
  domainName: DomainName;
  apiKey?: string;
}

function getNrdUrl(): string {
  const rawEnv = process.env.VITE_APP_ENVIRONMENT;
  const env = rawEnv === 'localhost' ? 'dev' : rawEnv;
  return env === 'prod' || env === 'production'
    ? 'https://nrd-api3.shutterfly.com/nrd/log'
    : `https://nrd-api3.${env}.shutterfly.com/nrd/log`;
}

function getMarkTime(name: string): number | undefined {
  const entries = performance.getEntriesByName(name, 'mark');
  return entries.length > 0 ? entries[entries.length - 1].startTime : undefined;
}

function computeTimings(
  authResult: AuthResult,
): Record<string, number | undefined> {
  const pollStart = getMarkTime(MARKS.POLL_START);
  const zuulFound = getMarkTime(MARKS.ZUUL_FOUND);
  const zuulTimeout = getMarkTime(MARKS.ZUUL_TIMEOUT);
  const initStart = getMarkTime(MARKS.INIT_START);
  const initDone = getMarkTime(MARKS.INIT_DONE);
  const getTokenStart = getMarkTime(MARKS.GET_TOKEN_START);
  const getTokenDone = getMarkTime(MARKS.GET_TOKEN_DONE);
  const authResolved = getMarkTime(MARKS.AUTH_RESOLVED);

  const pollEnd = authResult === 'RESOLVED' ? zuulFound : zuulTimeout;
  const totalEnd = authResult === 'RESOLVED' ? authResolved : zuulTimeout;

  return {
    zuulPollDuration_ms:
      pollStart != null && pollEnd != null
        ? Math.round(pollEnd - pollStart)
        : undefined,
    zuulInitDuration_ms:
      initStart != null && initDone != null
        ? Math.round(initDone - initStart)
        : undefined,
    getTokenDuration_ms:
      getTokenStart != null && getTokenDone != null
        ? Math.round(getTokenDone - getTokenStart)
        : undefined,
    totalDuration_ms:
      pollStart != null && totalEnd != null
        ? Math.round(totalEnd - pollStart)
        : undefined,
  };
}

function postToNrd(
  domainName: DomainName,
  authResult: AuthResult,
  apiKey?: string,
): void {
  const timings = computeTimings(authResult);
  const payload = {
    appName: 'universal-header-microfrontend',
    serviceLogVersion: 'v2',
    brandId: 'SFLY',
    logEvents: [{ eventType: 'header-auth-timing' }],
    appContext: {
      ...timings,
      domainName,
      authResult,
    },
  };

  const headers: Record<string, string> = {
    'Content-Type': 'application/json; charset=UTF-8',
  };
  if (apiKey) {
    headers['x-api-key'] = apiKey;
  }

  fetch(getNrdUrl(), {
    method: 'POST',
    body: JSON.stringify(payload),
    headers,
  }).catch(() => {
    // fire-and-forget — do not surface errors to the user
  });
}

const HeaderTimingReporter: React.FC<HeaderTimingReporterProps> = ({
  domainName,
  apiKey,
}) => {
  const hasFiredRef = useRef(false);

  useEffect(() => {
    if ((window as any).sfly_disable_header_timing) return;
    if (hasFiredRef.current) return;

    const handleEntries = (list: PerformanceObserverEntryList) => {
      if (hasFiredRef.current) return;

      const names = new Set(list.getEntries().map((e) => e.name));
      let authResult: AuthResult | null = null;

      if (names.has(MARKS.AUTH_RESOLVED)) {
        authResult = 'RESOLVED';
      } else if (names.has(MARKS.ZUUL_TIMEOUT)) {
        authResult = 'ZUUL_TIMEOUT';
      }

      if (authResult) {
        hasFiredRef.current = true;
        postToNrd(domainName, authResult, apiKey);
      }
    };

    const observer = new PerformanceObserver(handleEntries);
    observer.observe({ type: 'mark', buffered: true });

    return () => observer.disconnect();
  }, [domainName, apiKey]);

  return null;
};

export default HeaderTimingReporter;
